import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BatteryLow, Salad, Candy, Brain, CloudLightning, Frown, HeartCrack, Check } from 'lucide-react';
import { ZodiacAquariusIcon, StomachIcon } from '../../../theme/CustomIcons';
import { SHELL_WHITE } from '../../../theme/colors';
import { useScrollStage } from '../../../hooks/useScrollStage';

// Mirrors the real Signal Grid (src/screens/TrendsScreen.js "Body Stressors" /
// real signal picker in src/components/tracker/SignalLogModal.js) as static
// glass buttons over the Feature card photo. Desktop keeps all 9 signals in a
// 3x3 grid; mobile drops Workout Fatigue and reflows to 2x4 — a deliberate
// per-breakpoint content difference the user asked for, not just a resize.
// On mobile (progress prop present) the grid animates: buttons render in
// quickly one by one, "Sugar Craving" selects, then the grid gives way to a
// success overlay — mirroring SignalLogModal's own "Logged!" success
// choreography. (An earlier shine-sweep beat was cut as superfluous.)
const ALL_SIGNALS = [
  { label: 'Energy Crash', Icon: BatteryLow },
  { label: 'Hunger Spike', Icon: Salad },
  { label: 'Sugar Craving', Icon: Candy },
  { label: 'Brain Fog', Icon: Brain },
  { label: 'Wired but Tired', Icon: ZodiacAquariusIcon },
  { label: 'Irritability', Icon: CloudLightning },
  { label: 'Workout Fatigue', Icon: HeartCrack },
  { label: 'Nausea', Icon: Frown },
  { label: 'Stomach Issues', Icon: StomachIcon },
];

// Desktop cell size is noticeably smaller than the 1:1 Paper spec (104px) —
// at 104px this 3x3 grid overflowed the Feature card's fixed 644x580 frame
// and the bottom row got clipped. Diverging from Paper here on purpose.
const SIZES = {
  desktop: { cell: 80, cols: 3, iconSize: 14, labelSize: 11, gap: 8 },
  mobile: { cell: undefined, cols: 2, iconSize: 22, labelSize: 12, gap: 10, cellHeight: 100 },
};

// Selected-state values ported verbatim from the real product's own signal
// picker (SignalLogModal.js's SignalButton) rather than inventing a new look.
// `entered`/`delay` drive the mobile entrance stagger DIRECTLY on this root
// div (not a wrapping div) — only a direct flex child's own `flex` property
// affects the grid's column sizing, so wrapping this in another div broke
// the fixed 2-column layout (each button shrank to its label's natural width).
const SignalButton = ({ label, Icon, s, selected, entered = true, delay = 0 }) => (
  <div
    style={{
      width: s.cell,
      height: s.cellHeight ?? s.cell,
      flex: s.cell ? '0 0 auto' : `0 0 calc(50% - ${s.gap / 2}px)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s.cell ? 7 : 8,
      padding: s.cell ? '12px 8px' : '14px 10px',
      borderRadius: 16,
      backdropFilter: 'blur(8px)',
      backgroundColor: selected ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.10)',
      border: selected ? '2px solid rgba(255,255,255,0.75)' : '2px solid rgba(255,255,255,0.2)',
      boxShadow: selected ? '0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)' : 'none',
      opacity: entered ? 1 : 0,
      transform: entered ? 'translateY(0)' : 'translateY(8px)',
      transition: `opacity 0.3s ease ${delay}s, transform 0.3s ease ${delay}s, background-color 0.18s ease, border 0.18s ease, box-shadow 0.18s ease`,
    }}
  >
    <Icon size={s.iconSize} color={SHELL_WHITE} strokeWidth={selected ? 2.0 : 1.8} />
    <span style={{ fontFamily: 'DM Sans', fontSize: s.labelSize, fontWeight: 500, color: SHELL_WHITE, opacity: selected ? 1 : 0.85, textAlign: 'center', transition: 'opacity 0.18s ease' }}>
      {label}
    </span>
  </div>
);

const gridContainerStyle = (variant, s) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: s.gap,
  width: variant === 'desktop' ? s.cell * s.cols + s.gap * (s.cols - 1) : '100%',
});

const StaticSignalGrid = ({ variant, s, signals }) => (
  <div style={gridContainerStyle(variant, s)}>
    {signals.map((sig) => (
      <SignalButton key={sig.label} label={sig.label} Icon={sig.Icon} s={s} selected={false} />
    ))}
  </div>
);

// Beats (progress 0-1 across the card's pinned scroll runway):
// 0.00-0.06 hold — buttons not displayed yet
// 0.06 -> buttons render in quickly, one by one (staggered CSS entrance)
// 0.06-0.35 hold once all are visible
// 0.35 -> "Sugar Craving" taps selected
// 0.35-0.62 hold on the selection
// 0.62-1.00 buttons fade out, crossfading directly into the success overlay
// ("Logged!" + checkmark, ported from SignalLogModal) — ONE threshold drives
// both the grid's exit and the success's entrance via AnimatePresence, so
// there's no gap where neither is visible.
const AnimatedSignalGrid = ({ variant, s, signals, progress }) => {
  const rows = Math.ceil(signals.length / s.cols);
  const rowHeight = s.cell ?? s.cellHeight;
  const minHeight = rows * rowHeight + (rows - 1) * s.gap;

  const entered = useScrollStage(progress, 0.06);
  const sugarSelected = useScrollStage(progress, 0.35);
  const showSuccess = useScrollStage(progress, 0.62);

  return (
    <div style={{ position: 'relative', width: variant === 'desktop' ? s.cell * s.cols + s.gap * (s.cols - 1) : '100%', minHeight }}>
      <AnimatePresence mode="wait">
        {!showSuccess ? (
          <motion.div
            key="grid"
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.25 }}
            style={{ ...gridContainerStyle(variant, s), position: 'absolute', inset: 0, width: '100%' }}
          >
            {signals.map((sig, i) => (
              <SignalButton
                key={sig.label}
                label={sig.label}
                Icon={sig.Icon}
                s={s}
                selected={sig.label === 'Sugar Craving' && sugarSelected}
                entered={entered}
                delay={i * 0.03}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="signal-success"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', stiffness: 420, damping: 22, mass: 0.8 }}
            style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 18, delay: 0.05 }}
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.22)',
                border: '2px solid rgba(255,255,255,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Check size={30} color={SHELL_WHITE} strokeWidth={2.5} />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18, duration: 0.3 }}
              style={{ fontFamily: 'DM Sans', fontSize: 18, fontWeight: 600, color: SHELL_WHITE, textShadow: '0 2px 8px rgba(0,0,0,0.2)', margin: 0 }}
            >
              Logged!
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SignalGrid = ({ variant = 'desktop', progress }) => {
  const s = SIZES[variant];
  const signals = variant === 'mobile' ? ALL_SIGNALS.filter((sig) => sig.label !== 'Workout Fatigue') : ALL_SIGNALS;

  if (!progress) return <StaticSignalGrid variant={variant} s={s} signals={signals} />;
  return <AnimatedSignalGrid variant={variant} s={s} signals={signals} progress={progress} />;
};

export default SignalGrid;
